<script>
import {months, currentMonth} from '@/helpers/months';
import Datepicker from "vuejs-datepicker";
import {es} from "vuejs-datepicker/dist/locale";

import {
  channels,
  attentionLevels,
  pori,
  complaintStatus,
  personTypes,
  genders,
  complaintAnswers,
  penalties
} from '@/helpers/complaits/catalogs'
import SepomexAddressForm from "@components/support-areas/internal-control/condusef/sepomex-address-form.vue";
import CondusefProductForm from "@components/support-areas/internal-control/condusef/condusef-product-form.vue";

export default {
  name: "complaint-form",
  components: {
    CondusefProductForm,
    SepomexAddressForm,
    Datepicker
  },
  data: () => ({
    es,
    months,
    channels,
    attentionLevels,
    pori,
    complaintStatus,
    genders,
    complaintAnswers,
    penalties,
    personTypes,
    errors: {},
    generalError: null,
    form: {
      noTrim: null,
      num: 1,
      folio: "",
      fecRecepcion: "",
      fecAtencion: null,
      medioId: 1,
      product: "",
      causaId: 1,
      pori: "NO",
      estatus: 1,
      estadosId: null,
      nivelATId: null,
      munId: null,
      locId: null,
      colId: null,
      cp: null,
      tipoPersona: 1,
      quejasRespuesta: null,
      sexo: null,
      edad: null,
      fecResolucion: null,
      fecNotificacion: null,
      numPenal: null,
      penalizacionId: null
    },
  }),
  computed: {
    currentMonth
  },
  mounted() {
    this.form.noTrim = this.currentMonth - 1;
  },
  methods: {
    fieldError(field) {
      const value = this.errors?.[field];

      if (Array.isArray(value)) {
        return value[0] ?? null;
      }

      return typeof value === 'string' ? value : null;
    },
    clearErrors() {
      this.errors = {};
      this.generalError = null;
    },
    productsChangedHandler(form) {
      this.form.product = form.product;
      this.form.causaId = form.causaId;
    },
    async submitComplaint() {
      try {
        this.clearErrors();
        await axios.post('/api/condusef/redeco/complaint', this.form)
        this.$vs.notify({
          title: "Éxito",
          text: "Queja registrada",
          color: "success",
          iconPack: "feather",
          icon: "icon-check"
        });
      } catch (e) {
        if (e?.response?.status === 422) {
          const data = e.response.data ?? {};
          const looksLikeDirectErrors =
              data &&
              typeof data === 'object' &&
              !Array.isArray(data) &&
              !('errors' in data) &&
              !('message' in data);

          this.errors = looksLikeDirectErrors ? data : (data.errors ?? {});
          this.generalError = data.message ?? 'Verifica los campos marcados.';

          this.$vs.notify({
            title: "Faltan datos",
            text: this.generalError,
            color: "warning",
            iconPack: "feather",
            icon: "icon-alert-triangle"
          });
          return;
        }

        this.$vs.notify({
          title: "Error",
          text: e?.response?.data?.error ?? 'Ocurrió un error al guardar la queja.',
          color: "danger",
          iconPack: "feather",
          icon: "icon-alert-circle"
        });
      }
    },
    addressChangedHandler(address) {
      this.form.locId = address.locId;
      this.form.cp = address.cp;
      this.form.estadosId = address.estadosId;
      this.form.munId = address.munId;
      this.form.colId = address.colId;
    }
  },
}
</script>

<template>
  <div>
    <vs-alert
        color="danger"
        class="mb-4"
        v-if="generalError">
      {{ generalError }}
    </vs-alert>
    <div class="flex gap-4">
      <div class="w-6/12">
        <condusef-product-form @onChange="productsChangedHandler"/>

        <vx-card title="Datos de la solicitud" class="mt-3">
          <div class="grid grid-cols-2 gap-4">
            <vs-input
                class="w-full"
                label="Folio"
                v-model="form.folio"
                :danger="!!fieldError('folio')"
                :danger-text="fieldError('folio')"/>
            <vs-input
                class="w-full"
                label="Número de quejas"
                v-model="form.num"
                :danger="!!fieldError('num')"
                :danger-text="fieldError('num')"/>
            <vs-select
                class="w-full"
                label="Mes a reportar"
                v-model="form.noTrim"
                :danger="!!fieldError('noTrim')"
                :danger-text="fieldError('noTrim')">
              <vs-select-item
                  v-for="month in months"
                  :key="month.value"
                  :value="month.value"
                  :text="month.text"/>
            </vs-select>
            <vs-input
                type="date"
                class="w-full"
                label="Fecha recepción"
                v-model="form.fecRecepcion"
                :danger="!!fieldError('fecRecepcion')"
                :danger-text="fieldError('fecRecepcion')"/>

            <vs-select
                class="w-full"
                label="Estatus"
                v-model="form.estatus"
                :danger="!!fieldError('estatus')"
                :danger-text="fieldError('estatus')">
              <vs-select-item
                  v-for="status in complaintStatus"
                  :key="status.id"
                  :value="status.id"
                  :text="status.name"/>
            </vs-select>
            <vs-input
                type="date"
                class="w-full"
                label="Fecha atención"
                v-model="form.fecAtencion"
                v-if="form.estatus === 2"
                :danger="!!fieldError('fecAtencion')"
                :danger-text="fieldError('fecAtencion')"/>
            <vs-select
                class="w-full"
                label="Medio de Recepción"
                v-model="form.medioId"
                :danger="!!fieldError('medioId')"
                :danger-text="fieldError('medioId')">
              <vs-select-item
                  v-for="channel in channels"
                  :key="channel.id"
                  :value="channel.id"
                  :text="channel.name"/>
            </vs-select>
            <vs-select
                class="w-full"
                label="Nivel de atención"
                v-model="form.nivelATId"
                :danger="!!fieldError('nivelATId')"
                :danger-text="fieldError('nivelATId')">
              <vs-select-item
                  v-for="attentionLevel in attentionLevels"
                  :key="attentionLevel.id"
                  :value="attentionLevel.id"
                  :text="attentionLevel.name"/>
            </vs-select>
            <vs-select
                class="w-full"
                label="PORI"
                v-model="form.pori"
                :danger="!!fieldError('pori')"
                :danger-text="fieldError('pori')">
              <vs-select-item
                  v-for="p in pori"
                  :key="p.id"
                  :value="p.id"
                  :text="p.name"/>
            </vs-select>
            <vs-select
                class="w-full"
                label="Tipo de penalización"
                v-model="form.penalizacionId"
                :danger="!!fieldError('penalizacionId')"
                :danger-text="fieldError('penalizacionId')">
              <vs-select-item
                  v-for="penalty in penalties"
                  :key="penalty.id"
                  :value="penalty.id"
                  :text="penalty.name"/>
            </vs-select>
            <vs-input
                class="w-full"
                label="No. de penalizaciones"
                v-model="form.numPenal"
                v-if="form.penalizacionId !== null"
                :danger="!!fieldError('numPenal')"
                :danger-text="fieldError('numPenal')"/>
            <vs-input
                type="date"
                class="w-full"
                label="F. de notificación"
                v-model="form.fecNotificacion"
                v-if="form.estatus === 2"
                :danger="!!fieldError('fecNotificacion')"
                :danger-text="fieldError('fecNotificacion')"/>
            <vs-input
                type="date"
                class="w-full"
                label="F. de resolución"
                v-model="form.fecResolucion"
                v-if="form.estatus === 2"
                :danger="!!fieldError('fecResolucion')"
                :danger-text="fieldError('fecResolucion')"/>
            <vs-select
                class="w-full"
                label="Sentido de la resolución"
                v-model="form.quejasRespuesta"
                v-if="form.estatus === 2"
                :danger="!!fieldError('quejasRespuesta')"
                :danger-text="fieldError('quejasRespuesta')">
              <vs-select-item
                  v-for="answer in complaintAnswers"
                  :key="answer.id"
                  :value="answer.id"
                  :text="answer.name"/>
            </vs-select>
          </div>
        </vx-card>
      </div>
      <div class="w-6/12">
        <div class="">
          <sepomex-address-form @onChange="addressChangedHandler"/>
          <vx-card title="Datos de la persona" class="mt-3">
            <div class="grid grid-cols-3 gap-3">
              <vs-select
                  class="w-full"
                  label="Tipo persona"
                  v-model="form.tipoPersona"
                  :danger="!!fieldError('tipoPersona')"
                  :danger-text="fieldError('tipoPersona')">
                <vs-select-item
                    v-for="personType in personTypes"
                    :key="personType.id"
                    :value="personType.id"
                    :text="personType.name"/>
              </vs-select>
              <vs-select
                  class="w-full"
                  label="Género"
                  v-model="form.sexo"
                  v-if="form.tipoPersona === 1"
                  :danger="!!fieldError('sexo')"
                  :danger-text="fieldError('sexo')">
                <vs-select-item
                    v-for="gender in genders"
                    :key="gender.id"
                    :value="gender.id"
                    :text="gender.nombre"/>
              </vs-select>
              <vs-input
                  type="number"
                  class="w-full"
                  label="Edad"
                  v-model="form.edad"
                  v-if="form.tipoPersona === 1"
                  :danger="!!fieldError('edad')"
                  :danger-text="fieldError('edad')"/>
            </div>
          </vx-card>
        </div>

      </div>
    </div>
    <vs-button
        class="mt-4"
        @click="submitComplaint">
      Generar
    </vs-button>
  </div>
</template>